import { OTHER_ID, type FollowupAnswerInput, type FollowupView, type Lang } from '@aera/schema';
import { tidy } from '@aera/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorNote, Spinner } from '../components/ui.js';
import { Footer, Page } from './Chrome.js';
import { Chip, OptionCard, OtherField, pickOther } from './questions/common.js';

/** If the follow-up job never reports (worker down), the client is not held hostage by it. */
export const FOLLOWUP_WAIT_LIMIT_MS = 30000;
/** How often the waiting screen asks the server, in case the stream missed the follow-ups event. */
export const FOLLOWUP_POLL_MS = 3000;

/**
 * Up to three questions the model added after the open floor, under 根据您的作答，我们还有 N 个补充问题.
 * Same screen type as the bank: options as cards, text with its options as chips.
 */
export function Followups({
  followups,
  ready,
  lang,
  onAnswer,
  onDone,
  onBack,
}: {
  followups: FollowupView[];
  ready: boolean;
  lang: Lang;
  onAnswer: (id: string, input: FollowupAnswerInput) => Promise<unknown>;
  onDone: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, FollowupAnswerInput>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (ready) return undefined;
    const id = setTimeout(() => setTimedOut(true), FOLLOWUP_WAIT_LIMIT_MS);
    return () => clearTimeout(id);
  }, [ready]);

  useEffect(() => {
    if ((ready && followups.length === 0) || (!ready && timedOut)) doneRef.current();
  }, [ready, followups.length, timedOut]);

  if (!ready || followups.length === 0) {
    return (
      <Page className="flex min-h-[60svh] items-center justify-center">
        <Spinner label={t('followups.waiting')} />
      </Page>
    );
  }

  const fu = followups[Math.min(index, followups.length - 1)]!;
  const draft: FollowupAnswerInput = drafts[fu.id] ?? {
    selected: fu.answer?.selected ?? [],
    ...(fu.answer?.other ? { other: fu.answer.other } : {}),
    ...(fu.answer?.text ? { text: fu.answer.text } : {}),
    skipped: false,
  };
  const set = (next: Partial<FollowupAnswerInput>) => {
    setError(null);
    setDrafts((d) => ({ ...d, [fu.id]: { ...draft, ...next, skipped: false } }));
  };

  const submit = async (skip: boolean) => {
    setBusy(true);
    try {
      await onAnswer(fu.id, skip ? { selected: [], skipped: true } : draft);
      if (index + 1 < followups.length) setIndex(index + 1);
      else onDone();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const options = fu.options;
  const withOther = fu.allowOther && fu.type !== 'text';
  const multi = fu.type === 'multi';
  const toggle = (id: string) => {
    if (!multi) return set({ selected: [id], other: undefined });
    set({ selected: draft.selected.includes(id) ? draft.selected.filter((s) => s !== id) : [...draft.selected, id] });
  };
  const typeOther = (other: string) => set({ selected: pickOther(draft.selected, other, multi, () => false), other });
  const insertChip = (label: string) => {
    const current = draft.text ?? '';
    set({ text: current ? `${current} ${label}` : label });
  };

  return (
    <>
      <Page>
        <p className="text-[14px] font-medium text-indigo">
          {t('followups.header', { count: followups.length })}
          <span className="ml-2 text-ink-2">{t('followups.of', { n: index + 1, count: followups.length })}</span>
        </p>
        <h1 id={`fu-${fu.id}-label`} className="mt-3 text-[24px] font-semibold leading-[1.3] tracking-[-0.01em] sm:text-[28px]">
          {tidy(fu.prompt)}
        </h1>
        <div className="mt-6 space-y-3">
          {fu.type === 'text' ? (
            <>
              {fu.options.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {fu.options.map((o) => (
                    <Chip key={o.id} role="button" onClick={() => insertChip(o.label)}>
                      {o.label}
                    </Chip>
                  ))}
                </div>
              )}
              <textarea
                aria-labelledby={`fu-${fu.id}-label`}
                rows={4}
                maxLength={2000}
                value={draft.text ?? ''}
                placeholder={t('question.textPlaceholder')}
                onChange={(e) => set({ text: e.target.value })}
                className="w-full rounded-2xl border border-hair bg-white px-4 py-3 text-[16px] outline-none focus:border-indigo"
              />
            </>
          ) : (
            <div role={multi ? 'group' : 'radiogroup'} aria-labelledby={`fu-${fu.id}-label`} className="grid gap-2.5">
              {options.map((o, i) => (
                <OptionCard key={o.id} id={`opt-${fu.id}-${i + 1}`} role={multi ? 'checkbox' : 'radio'} checked={draft.selected.includes(o.id)} onClick={() => toggle(o.id)}>
                  {tidy(o.label)}
                </OptionCard>
              ))}
              {withOther && <OtherField id={`opt-${fu.id}-${options.length + 1}`} lang={lang} checked={draft.selected.includes(OTHER_ID)} value={draft.other ?? ''} label={t('question.otherField', { lng: lang })} onChange={typeOther} />}
            </div>
          )}
        </div>
        <button type="button" onClick={() => void submit(true)} className="mt-6 text-[14px] text-ink-2 underline-offset-4 hover:text-black hover:underline">
          {t('question.skip')}
        </button>
        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
      </Page>
      <Footer onBack={index > 0 ? () => setIndex(index - 1) : onBack} onNext={() => void submit(false)} busy={busy} />
    </>
  );
}
